import type { PopoverProps } from "@mui/material";
import { atom, atomFamily } from "recoil";

import type { IExtendableDto } from "../../dto";
import type {
  ColumnOrderState,
  ColumnSizingState,
  ExpandedStateList,
  GroupingState,
  PaginationState,
  SortingState,
  VisibilityState,
} from "../../helpers/react-table.features";
import type {
  IGroupResult,
  IGroupValue,
  ITableFixedColumnNames,
  RydeUiUnit,
  TRideDtoStartEndKey,
} from "../../models";

export const tableColumnsOrderStateAF = atomFamily<
  ColumnOrderState,
  RydeUiUnit
>({
  key: "tableColumnsOrderStateAF",
  default: [],
});

export const tableColumnsVisibilityAF = atomFamily<VisibilityState, RydeUiUnit>(
  {
    key: "tableColumnsVisibilityAF",
    default: {},
  },
);

/** Px width per column id; empty while the grid still stretches its default widths to fill the screen. */
export const tableColumnsSizingAF = atomFamily<ColumnSizingState, RydeUiUnit>({
  key: "tableColumnsSizingAF",
  default: {},
});

export const tableColumnsSortingAF = atomFamily<SortingState, RydeUiUnit>({
  key: "tableColumnsSortingAF",
  default: [],
});

export const tableGroupingDirectionDescAF = atomFamily<boolean, RydeUiUnit>({
  key: "tableGroupingDirectionDescAF",
  default: false,
});

export const tableGroupingAF = atomFamily<GroupingState, RydeUiUnit>({
  key: "tableGroupingAF",
  default: [],
});

export const tableServerGroupValuesPagesAf = atomFamily<number, RydeUiUnit>({
  key: "tableServerGroupValuesPagesAf",
  default: 0,
});

export const tableServerGroupValuesPageAf = atomFamily<number, RydeUiUnit>({
  key: "tableServerGroupValuesPageAf",
  default: 0,
});

export const tableServerGroupValuesAllAf = atomFamily<
  IGroupValue[],
  RydeUiUnit
>({
  key: "tableServerGroupValuesAllAF",
  default: () => [],
});

export const tableServerGroupValuesAf = atomFamily<IGroupValue[], RydeUiUnit>({
  key: "tableServerGroupValuesAF",
  default: () => [],
});

export const tableRowsGroupValuesAtomFamily = atomFamily<
  IGroupResult<IExtendableDto[]>[],
  RydeUiUnit
>({
  key: "tableRowsGroupValuesAtomFamily",
  default: () => [],
});

export const tableSettingsDrawerAF = atomFamily<boolean, RydeUiUnit>({
  key: "tableSettingsDrawerAF",
  default: false,
});

export const bulkOperationsDrawerAF = atomFamily<boolean, RydeUiUnit>({
  key: "bulkOperationsDrawerAF",
  default: false,
});

export const bulkRecalculatePriceOptionsDrawerAF = atomFamily<
  boolean,
  RydeUiUnit
>({
  key: "bulkRecalculatePriceOptionsDrawerAF",
  default: false,
});

export const bulkRecalculateSupplierPriceOptionsDrawerAF = atomFamily<
  boolean,
  RydeUiUnit
>({
  key: "bulkRecalculateSupplierPriceOptionsDrawerAF",
  default: false,
});

export const bulkRebuildRideDrawerAtom = atom<boolean>({
  key: "bulkRebuildRideDrawerAtom",
  default: false,
});

export const bulkRebuildRoutesCustomDrawerAtom = atom<boolean>({
  key: "bulkRebuildRoutesCustomDrawerAtom",
  default: false,
});

export const bulkUpdateStationsDrawerAtom = atom<boolean>({
  key: "bulkUpdateStationsDrawerAtom",
  default: false,
});

export const bulkAIAdvisorDrawerAF = atomFamily<boolean, RydeUiUnit>({
  key: "bulkAIAdvisorDrawerAF",
  default: false,
});

export const bulkUpdateFormDrawerAF = atomFamily<boolean, RydeUiUnit>({
  key: "bulkUpdateFormDrawerAF",
  default: false,
});

export const bulkCloneFormDrawerAF = atomFamily<boolean, RydeUiUnit>({
  key: "bulkCloneFormDrawerAF",
  default: false,
});

export const bulkJourneyAssignFormDrawerAF = atomFamily<boolean, RydeUiUnit>({
  key: "bulkJourneyAssignFormDrawerAF",
  default: false,
});

export const gridExpandedAF = atomFamily<ExpandedStateList, RydeUiUnit>({
  key: "gridExpandedAtom",
  default: {},
});

export const tableElementRefAtom = atom<null | HTMLTableElement>({
  key: "tableElementRefAtom",
  default: null,
});

export const tableItemPaginationAF = atomFamily<
  PaginationState | null,
  [groupingValue: string, RydeUiUnit]
>({
  key: "tableItemPaginationAF",
  default: null,
});

export interface IRideStartEndPosition {
  x: number;
  y: number;
  isFieldOpened: boolean;
  rideId: null | number;
  fieldName: null | TRideDtoStartEndKey;
  iconAnchorElement: PopoverProps["anchorEl"];
}

export const rideStartEndPositionAtom = atom<IRideStartEndPosition>({
  key: "rideStartEndPositionAtom",
  default: {
    x: 0,
    y: 0,
    isFieldOpened: false,
    rideId: null,
    fieldName: null,
    iconAnchorElement: null,
  },
});

export const fixedColumnNamesAF = atomFamily<
  ITableFixedColumnNames,
  RydeUiUnit
>({
  key: "fixedColumnNamesAF",
  default: { fixedColumns: [], lastColumns: [] },
});
